import type { ReactNode } from "react";

/**
 * PageShell — MS253 shared content frame for authed-dashboard pages.
 *
 * Lucky's feedback (2026-05-18): "Headliner texts, cards, pills are
 * all stretched to the corners of the frames they are built within...
 * every UX specialist would agree you would try to create some space
 * so that things can breathe, and right now everything feels quite
 * claustrophobic, quite congested."
 *
 * Pre-MS253, every dashboard page rendered its content directly
 * inside ConciergeLayout's `<Outlet />` with NO horizontal padding
 * or max-width — so the PageHeader, CTAs, and grid cards all stuck
 * to the literal edges of the viewport on wide screens.
 *
 * This component is the universal breathing-room container.
 *
 * MS581 — Phase 4 width-ladder discipline (2026-05-28):
 *
 * The four variants now map onto three distinct content shapes:
 *
 *   narrow  (max-w-3xl, 768px) — single-column form / reading-width
 *                                 pages. Settings, Notifications, Refer.
 *                                 Stays narrow even on 1920px monitors
 *                                 because long-form text + form inputs
 *                                 break down past ~80 chars/line.
 *
 *   default (max-w-5xl, 1024px) — content-heavy pages with cards but
 *                                 not table-y or tile-grid. Wishlist.
 *                                 The "wide-ish" middle tier per the
 *                                 MS581 spec.
 *
 *   wide    (max-w-6xl, 1152px) — table/canvas surfaces — Orders rows,
 *                                 Calendar month grid, Team roster.
 *                                 Grows with the viewport up through
 *                                 the `xl:` tier.
 *
 *   full    (max-w-none)        — no max-width cap. Tile-grid /
 *                                 workbench pages (Dashboard's split
 *                                 grid, Browse's catalog) that own
 *                                 their own outer width discipline.
 *                                 New addition for the Phase 4
 *                                 dashboard rebuild — pages that
 *                                 want to exploit ≥1920px monitors
 *                                 without an artificial inner cap.
 *
 * The component also provides:
 *   - px-4 sm:px-6 lg:px-8 — responsive horizontal padding so the
 *     content has consistent inset at every breakpoint.
 *   - py-8 — vertical breathing room above + below.
 *
 * Pages opt in by wrapping their content. Dashboard.tsx with its
 * complex split-grid layout opts OUT and provides its own framing.
 *
 * MS582–MS585 will sweep through individual pages to apply the
 * `full` variant on tile-grid surfaces. MS581 itself only ships the
 * primitive — no page is touched here.
 */

export type PageShellWidth = "narrow" | "default" | "wide" | "full";

const WIDTH_CLASS: Record<PageShellWidth, string> = {
  narrow: "max-w-3xl", // 768px — single-column form / reading-width pages
  default: "max-w-5xl", // 1024px — content-heavy card pages
  wide: "max-w-6xl", // 1152px — table-y / canvas pages (Orders, Calendar, Team)
  full: "max-w-none", // no cap — tile-grid / workbench pages (Dashboard, Browse)
};

export interface PageShellProps {
  children: ReactNode;
  /** Max-width preset. Default 'default' (max-w-5xl / 1024px). */
  width?: PageShellWidth;
  /** Extra classes to append to the inner container (rare). */
  className?: string;
}

export function PageShell({ children, width = "default", className = "" }: PageShellProps) {
  return (
    <div className={`mx-auto px-4 sm:px-6 lg:px-8 py-8 ${WIDTH_CLASS[width]} ${className}`.trim()}>
      {children}
    </div>
  );
}
